/**
 * Web Audio tone synth + optional spoken prompts (see §4, §8).
 *
 * Each player owns one pentatonic note; any random sequence is automatically
 * consonant. Tones are warm (soft attack/decay) and gentle in level. The
 * AudioContext must be created/resumed from a user gesture (browser policy) —
 * call unlock() on the first tap/button.
 */

import { AUDIO } from '../shared/tuning.js';

let ctx: AudioContext | null = null;

export function unlockAudio(): void {
  if (!ctx) {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (AC) ctx = new AC();
  }
  if (ctx && ctx.state === 'suspended') void ctx.resume();
}

/** Play one warm tone at `freq` Hz. `whenLocal` is a Date.now()-based time
 *  (defaults to now) so playback can be scheduled precisely against the clock. */
export function playTone(freq: number, whenLocal?: number): void {
  if (!ctx) unlockAudio();
  if (!ctx) return;
  const startAt = ctx.currentTime + Math.max(0, ((whenLocal ?? Date.now()) - Date.now()) / 1000);

  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(AUDIO.PEAK_GAIN, startAt + AUDIO.ATTACK_S);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + AUDIO.ATTACK_S + AUDIO.DECAY_S);

  const osc = ctx.createOscillator();
  osc.type = AUDIO.WAVE;
  osc.frequency.value = freq;
  osc.connect(gain);
  osc.start(startAt);
  osc.stop(startAt + AUDIO.ATTACK_S + AUDIO.DECAY_S + 0.05);

  // Optional fifth above for warmth (still consonant).
  if (AUDIO.ADD_FIFTH) {
    const g2 = ctx.createGain();
    g2.connect(ctx.destination);
    g2.gain.setValueAtTime(0.0001, startAt);
    g2.gain.exponentialRampToValueAtTime(AUDIO.PEAK_GAIN * AUDIO.FIFTH_GAIN, startAt + AUDIO.ATTACK_S);
    g2.gain.exponentialRampToValueAtTime(0.0001, startAt + AUDIO.ATTACK_S + AUDIO.DECAY_S);
    const o2 = ctx.createOscillator();
    o2.type = AUDIO.WAVE;
    o2.frequency.value = freq * 1.5;
    o2.connect(g2);
    o2.start(startAt);
    o2.stop(startAt + AUDIO.ATTACK_S + AUDIO.DECAY_S + 0.05);
  }
}

/** A happy ascending arpeggio for celebrations (consonant pentatonic run). */
export function playCelebration(freqs: number[]): void {
  if (!ctx) unlockAudio();
  if (!ctx) return;
  const base = Date.now();
  freqs.forEach((f, i) => playTone(f, base + i * 120));
  // A bright top note to finish.
  playTone(freqs[freqs.length - 1] * 2, base + freqs.length * 120);
}

/** A soft, low "let's try again" tone — never harsh, never a buzzer (§6, §8). */
export function playSoftRetry(): void {
  playTone(392.0); // G4, low and gentle
}

/**
 * Optional spoken prompt via the Web Speech API (assist + accessibility, §7/§8).
 * Silently no-ops where unsupported. Kept short and calm.
 */
export function speak(text: string): void {
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 1.05;
    u.volume = 0.9;
    synth.cancel(); // don't queue up; latest cue wins
    synth.speak(u);
  } catch {
    /* unsupported — ignore */
  }
}

/** Haptic buzz on the owning device (guarded for unsupported hardware, §4). */
export function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* ignore */
  }
}
